<script lang="ts" generics="TValues">
    import type { Field, Form } from '@jmvdevelop/svelte-form';
    import type { SearchHeaderFormData } from '$components/SearchHeader/SearchHeaderFormData';
    import { query } from '$lib/apollo/SvelteApollo';
    import { type WhereQueryQuery, WhereQueryDocument } from '$gql/graphql';
    import { type ComboboxOptionProps } from '@melt-ui/svelte';
    import type { IterableElement } from 'type-fest';
    import { match, P } from 'ts-pattern';
    import SearchHeaderComboxbox from '$components/SearchHeader/SearchHeaderComboxbox.svelte';
    import MobileModalCombobox, {
        createDialog,
    } from '$components/SearchHeader/MobileModalCombobox.svelte';
    import Highlighter from '$components/Highlighter.svelte';
    import MobileButton from '$components/SearchHeader/MobileButton.svelte';
    import SubmitButton from '$components/Form/SubmitButton.svelte';
    import { readable } from 'svelte/store';
    import StackZIndex from '$components/ZIndex/StackZIndex.svelte';
    import * as t from '$trans';
    import { fly } from 'svelte/transition';
    import { getContainer } from '$components/Context/ContainerContext';

    const { lang } = getContainer();

    export let form: Form<TValues, string>;
    export let queryField: Field<TValues, string | null>;
    export let itemField: Field<TValues, SearchHeaderFormData['whereItem'] | null>;
    export let showFiltersField: Field<TValues, boolean>;
    export let style: 'desktop' | 'desktop-full' | 'mobile' | 'mobile-full';
    export let withSearch: boolean = false;
    export let withFilters: boolean = false;
    export let forceIsSubmitting: boolean | null = null;

    const q = form.stores().fieldValue(queryField);

    let skip = true;
    $: response = skip
        ? { result: readable(null), setVariables: null }
        : query(WhereQueryDocument, {
              variables: { q: $q ?? '' },
          });
    $: ({ result, setVariables } = response);
    $: setVariables?.({ q: $q ?? '' });
    $: items = ($q ?? '').trim() === '' ? [] : ($result?.data?.searchHeader.where ?? []);

    function toOption(
        item: IterableElement<WhereQueryQuery['searchHeader']['where']>,
    ): ComboboxOptionProps<SearchHeaderFormData['whereItem']> {
        return match(item)
            .with(
                {
                    type: 'Country',
                    text: P.select('label'),
                    value: P.select('value'),
                },
                ({ value, label }) =>
                    ({
                        value: { countryId: value },
                        label,
                    }) as const,
            )
            .with(
                {
                    type: 'City',
                    text: P.select('label'),
                    value: P.select('value'),
                },
                ({ value, label }) =>
                    ({
                        value: { cityId: parseInt(value, 10) },
                        label,
                    }) as const,
            )
            .with(
                {
                    type: 'SubCity',
                    text: P.select('label'),
                    value: P.select('value'),
                },
                ({ value, label }) =>
                    ({
                        value: { subCityId: parseInt(value, 10) },
                        label,
                    }) as const,
            )
            .with(
                {
                    type: 'District',
                    text: P.select('label'),
                    value: P.select('value'),
                },
                ({ value, label }) =>
                    ({
                        value: { districtId: parseInt(value, 10) },
                        label,
                    }) as const,
            )
            .otherwise(() => ({
                value: null,
                label: '',
            }));
    }

    const giveFocusToField = (event: MouseEvent) => {
        if (event.target instanceof HTMLElement) {
            if (event.target.tagName === 'DIV') {
                const input = document.getElementById(form.getFieldId(queryField));
                input?.click();
                input?.focus();
            }
        }
    };

    const mobileDialog = createDialog(form, queryField, {});

    $: label = t.search_header.label_where({}, $lang);
    $: placeholder = t.search_header.placeholder_where({}, $lang);

    function onItemCanBeLoaded() {
        skip = false;
    }
</script>

{#if style === 'mobile'}
    <MobileButton
        {form}
        {queryField}
        {itemField}
        {label}
        {placeholder}
        dialog={mobileDialog}
        {withSearch}
    />

    <MobileModalCombobox
        {form}
        {queryField}
        {itemField}
        {items}
        {toOption}
        dialog={mobileDialog}
        {label}
        {placeholder}
        {onItemCanBeLoaded}
    >
        <svelte:fragment slot="item" let:item>
            <Highlighter
                text={item.text}
                query={$q}
                class="m-0 p-0"
                highlightClass="font-bold"
            />
        </svelte:fragment>
    </MobileModalCombobox>
{:else}
    <!-- svelte-ignore a11y-no-static-element-interactions a11y-click-events-have-key-events -->
    <div
        class="h-[64px] rounded-r-[50px] bg-white flex-1 cursor-pointer flex gap-[24px]"
        on:click={giveFocusToField}
    >
        <StackZIndex zIndex="current" let:zIndex>
            <SearchHeaderComboxbox
                {form}
                {queryField}
                {itemField}
                {items}
                {toOption}
                {label}
                {placeholder}
                zIndex={zIndex - 1}
                thinLeftPad={false}
                {onItemCanBeLoaded}
            >
                <svelte:fragment slot="item" let:item>
                    <Highlighter
                        text={item.text}
                        query={$q}
                        class="m-0 p-0"
                        highlightClass="font-bold"
                    />
                </svelte:fragment>
            </SearchHeaderComboxbox>
        </StackZIndex>

        {#if withFilters || withSearch}
            <div class="shrink-0 flex gap-[16px] items-center justify-center pr-[12px]">
                {#if withFilters}
                    <div transition:fly={{ duration: 150, y: -5 }}>
                        <button
                            class="btn-leo btn-leo-gray-blue btn-lg"
                            type="button"
                            on:click|preventDefault={() => {
                                form.dispatch((state) => {
                                    const bool = showFiltersField.getValue(state.values);
                                    return form
                                        .actions()
                                        .changeValue(!bool, showFiltersField)(state);
                                });
                            }}
                        >
                            <i class="tnp tnp-filters" aria-hidden={true}></i>
                            Filtres
                        </button>
                    </div>
                {/if}

                {#if withSearch}
                    <SubmitButton
                        {form}
                        layout="stable"
                        class="btn-leo btn-leo-primary btn-lg"
                        {forceIsSubmitting}
                        >{t.ui.search({}, $lang)}
                    </SubmitButton>
                {/if}
            </div>
        {/if}
    </div>
{/if}
